import React from "react";
import ModalFrame from "../../../../toolkit/modalFrame";
import { Container, Row, Col, Button } from "reactstrap";
import s from "./confirmModalDoc.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import {
  errorNotification,
  successNotification
} from "../../../../utils/notifications";
import CamMultiCapture from "../CamMultiCapture";
import {
  Archivo,
  CreateDocumentLinkRequest,
  FlujoDocumento,
  StatusAutorizacion
} from "../../expedientsV2controls/types";
import FormBuilderConfig from "../../../../toolkit/baseForms2/formBuilder/FormBuilder.config";
import FormBuilder from "../../../../toolkit/baseForms2/formBuilder/FormBuilder";
import IButtonBuilder from "../../../../toolkit/baseForms2/iButtonBuilder/IButtonBuilder";
import { Styled } from "../../../../toolkit/decorators/Artifact";
import { getPersonByIdRequest } from "../../../actions/persons";
import {
  createCincelDocumentLink,
  getCincelDocumentFirmantes
} from "../../../actions/expedientes";

interface Props {
  isOpen: boolean;
  title: String;
  archivo: Archivo;
  ok: (value: any) => void;
  cancel: () => void;
  success: () => void;
  linkConfirm?: string;
  urlUpload?: string;
  formConfig: FormBuilderConfig<any, "aceptar" | "cancelar">;
  message: string;
  formValues?: any;
}

interface State {
  value: any;
  wait: boolean;
  cameraMode: boolean;
  cincelHardConfirm: boolean;
  danger: string;
  processing: boolean;
  waiting: boolean;
  firmantes: any[];
  loadingFirmantes: boolean;
}

@Styled(s)
export default class ConfirmModalDoc extends React.Component<Props, State> {
  state = {
    value: null,
    wait: false,
    cameraMode: false,
    cincelHardConfirm: false,
    danger: null,
    processing: false,
    waiting: false,
    firmantes: [],
    loadingFirmantes: false
  };

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.startup() && this.findFirmantes();
    }
  }

  startup(): boolean {
    const cincelHardConfirm =
      this.props.archivo.flujo == FlujoDocumento.CINCEL &&
      this.props.archivo.statusAutorizacion != StatusAutorizacion.NO_GENERADO;
    const danger = cincelHardConfirm
      ? this.props.archivo.completo
        ? "El documento ya había sido firmado; SE SOLICITARÁ DE NUEVO POR CORREO Y SE IGNORARÁN LAS FIRMAS ACTUALES"
        : "Las firmas ya se han solicitado por correo; NO DEBERÍA HACER ESTO SI NO HA MODIFICADO LA DIRECCIÓN DEL CLIENTE"
      : null;

    this.setState({
      cincelHardConfirm,
      danger
    });

    return cincelHardConfirm;
  }

  handleFileChange = async evt => {
    evt.preventDefault();
    if (!evt || !evt.target || !evt.target.files || !evt.target.files.length) {
      errorNotification("Debe seleccionar un archivo");
      return;
    }

    const file = evt.target.files[0];
    const data = new FormData();
    data.append("file", file);

    this.setState({
      wait: true
    });

    try {
      await axios.post(this.props.urlUpload, data, {
        withCredentials: true
      });
      successNotification("Archivo cargado correctamente");
      this.props.success();
    } catch (error) {
      errorNotification("No se pudo cargar el archivo");
      this.props.cancel();
    }

    this.setState({
      wait: false
    });
  };

  formSubmit = async values => {
    try {
      this.setState({ processing: true });
      await this.props.ok(values);
      this.setState({ processing: false });
    } catch (err) {
      this.setState({ processing: false });
    }
  };

  findFirmantes = async () => {
    const archivo = this.props.archivo;

    if (archivo.completo) {
      return;
    }

    this.setState({ loadingFirmantes: true });
    try {
      const { data } = await getCincelDocumentFirmantes(
        archivo.idExpediente,
        archivo.idItem
      );

      this.setState({ firmantes: ((data || {}).firmantes || []).reverse() });
      this.setState({ loadingFirmantes: false });
    } catch (error) {
      this.setState({ loadingFirmantes: false });
      errorNotification(
        error && error.response && error.response.data
          ? error.response.data.message || "Error al obtener firmantes"
          : "Error al obtener firmantes"
      );
    }
  };

  openOrSendDocument = async (firmante: any, resend: boolean) => {
    this.setState({ waiting: true });

    try {
      const archivo = this.props.archivo;
      const idPersona = archivo.idPersona || archivo.idOwner || -1;
      let curp: string = null;

      if (!resend) {
        const { data: personaResult } = await getPersonByIdRequest(idPersona);

        if (!personaResult) {
          errorNotification("La persona asociada al documento no existe");
          this.setState({ waiting: false });
          return;
        }

        if ((personaResult.curp || "").length < 18) {
          errorNotification("La persona no cuenta con un curp válido");
          this.setState({ waiting: false });
          return;
        }

        curp = personaResult.curp;
      }

      const createCincelDocumentLinkRequest: CreateDocumentLinkRequest = {
        idExpediente: archivo.idExpediente,
        idItem: archivo.idItem,
        idPersona: firmante.idPersona,
        firmanteUuid: firmante.firmanteUuid,
        documentoUuid: firmante.documentoUuid,
        curp,
        email: firmante.email,
        nombre: firmante.nombre,
        resend
      };

      const { data: createLinkResult } = await createCincelDocumentLink(
        createCincelDocumentLinkRequest
      );

      if (!resend && createLinkResult && createLinkResult.url) {
        window.open(createLinkResult.url, "_blank");
      } else if (resend) {
        successNotification("Se ha reenviado el documento para firma");
      } else {
        errorNotification(
          resend
            ? "Error al reenviar el documento para firma."
            : "Error al abrir documento para firma."
        );
      }

      this.setState({ waiting: false });
    } catch (error) {
      this.setState({ waiting: false });
      const message = resend
        ? "Error al reenviar el documento para firma."
        : "Error al abrir documento para firma.";

      errorNotification(
        error && error.response && error.response.data
          ? error.response.data.message || message
          : message
      );
    }
  };

  render() {
    if (this.state.cincelHardConfirm) {
      return this.renderCincelHardConfirm();
    }

    return (
      <ModalFrame
        title={this.props.title}
        isOpen={this.props.isOpen}
        toggle={() => this.props.cancel()}
        size={this.props.archivo.flujo != FlujoDocumento.CINCEL ? "lg" : "md"}
      >
        <Container style={{ width: "90%" }} className="center-block p-0 mb-3">
          <Row>
            {!!this.state.danger && (
              <div className="col-12 alert alert-danger mb-3" role="alert">
                {this.state.danger}
              </div>
            )}
            <p className="col-12">{this.props.message}</p>
          </Row>

          <Row className="mt-2">
            {!this.props.formConfig && (
              <Col xs="12" md={this.props.urlUpload ? "4" : "6"}>
                <div>
                  <a
                    className={`${s.btnLink} ${s.primaryBtnLink}`}
                    href={this.props.linkConfirm}
                    onClick={e => {
                      if (this.state.wait) {
                        return false;
                      }

                      this.setState({
                        wait: true,
                        cameraMode: false
                      });

                      setTimeout(() => {
                        const { value } = this.state;
                        this.props.ok(value);
                        this.setState({
                          wait: false
                        });
                      }, 2000);
                      return true;
                    }}
                  >
                    {this.state.wait ? (
                      <FontAwesomeIcon icon={faSpinner} pulse />
                    ) : this.props.urlUpload ? (
                      "Escanear"
                    ) : (
                      "Aceptar"
                    )}
                  </a>
                </div>
              </Col>
            )}
            {this.props.urlUpload && (
              <Col xs="12" md={this.props.urlUpload ? "4" : "6"}>
                <a
                  className={`${s.btnLink} ${s.secundaryBtnLink}`}
                  onClick={e => {
                    if (this.state.wait) {
                      e.preventDefault();
                      e.stopPropagation();
                      this.setState({ cameraMode: false });
                      return false;
                    }
                  }}
                >
                  {this.state.wait ? (
                    <FontAwesomeIcon icon={faSpinner} pulse />
                  ) : (
                    "Seleccionar archivo"
                  )}
                  <input
                    className={s.inputFile}
                    type="file"
                    id={`scannedFile`}
                    accept=".pdf"
                    title=""
                    onChange={this.handleFileChange}
                  />
                </a>
              </Col>
            )}
            {this.props.urlUpload && (
              <Col xs="12" md={this.props.urlUpload ? "4" : "6"}>
                <a
                  className={`${s.btnLink} ${s.thirdBtnLink}`}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.setState({ cameraMode: true });
                  }}
                >
                  {this.state.wait ? (
                    <FontAwesomeIcon icon={faSpinner} pulse />
                  ) : (
                    "Tomar foto"
                  )}
                </a>
              </Col>
            )}
            {this.props.urlUpload && !!this.state.cameraMode && (
              <Col xs="12">
                <CamMultiCapture
                  exportButtonTitle="Subir archivo"
                  exportFile={async (pdf: File) => {
                    const data = new FormData();
                    data.append("file", pdf);

                    this.setState({
                      wait: true,
                      cameraMode: false
                    });

                    try {
                      await axios.post(this.props.urlUpload, data, {
                        withCredentials: true
                      });
                      successNotification("Archivo cargado correctamente");
                      this.props.success();
                    } catch (error) {
                      errorNotification("No se pudo cargar el archivo");
                      this.props.cancel();
                    }

                    this.setState({
                      wait: false
                    });
                  }}
                />
              </Col>
            )}
            {!this.props.formConfig && (
              <Col xs="12" md={this.props.urlUpload ? "12" : "6"}>
                <a
                  className={`${s.btnLink} ${s.cancelBtnLink}`}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (this.state.wait) {
                      return false;
                    }

                    this.setState({ value: null });
                    this.props.cancel();
                  }}
                >
                  {this.state.wait ? (
                    <FontAwesomeIcon icon={faSpinner} pulse />
                  ) : (
                    "Cancelar"
                  )}
                </a>
              </Col>
            )}
          </Row>
          {this.props.formConfig && (
            <FormBuilder
              config={this.props.formConfig}
              submit={this.formSubmit}
              processing={this.state.processing}
              init={this.props.formValues}
            >
              <IButtonBuilder name="cancelar" onClick={this.props.cancel} />
              {this.props.children}
            </FormBuilder>
          )}
        </Container>
      </ModalFrame>
    );
  }

  renderCincelHardConfirm = () => {
    if (this.state.loadingFirmantes) {
      return (
        <ModalFrame
          title={this.props.title}
          isOpen={this.props.isOpen}
          toggle={() => this.props.cancel()}
          size="md"
        >
          <Container style={{ width: "90%" }} className="center-block p-0 mb-3">
            <Row>
              <div className="col-12 alert alert-info mb-3" role="alert">
                <FontAwesomeIcon icon={faSpinner} pulse /> Cargando firmantes
              </div>
            </Row>
          </Container>
        </ModalFrame>
      );
    }

    return (
      <ModalFrame
        title={this.props.title}
        isOpen={this.props.isOpen}
        toggle={() => this.props.cancel()}
        size="md"
      >
        <Container style={{ width: "90%" }} className="center-block p-0 mb-3">
          <Row>
            {!!this.state.danger && (
              <div className="col-12 alert alert-danger mb-3" role="alert">
                {this.state.danger}
              </div>
            )}

            {!!this.state.firmantes && this.state.firmantes.length > 1 && (
              <div
                style={{
                  backgroundColor: "#eee",
                  fontSize: "11px",
                  fontWeight: "bold",
                  padding: "4px",
                  width: "100%",
                  textAlign: "center"
                }}
              >
                Recuerde que las firmas deben solicitarse en cierto orden.
              </div>
            )}
          </Row>
          {this.state.cincelHardConfirm &&
            this.state.firmantes.map(firmante => (
              <Row key={firmante.firmanteUuid} className="mt-2 mb-3">
                <Col xs="12" md="12">
                  <div
                    style={{
                      padding: "4px",
                      borderRadius: "5px",
                      backgroundColor: "#fff2cc"
                    }}
                  >
                    Firmante: {firmante.nombre}
                    <br />
                    <span style={{ fontSize: "12px" }}>{firmante.email}</span>
                  </div>
                </Col>
                <Col xs="12" md="12">
                  <Button
                    className={`${s.secundaryButton} mt-3`}
                    onClick={() => {
                      this.openOrSendDocument(firmante, false);
                    }}
                    block
                    disabled={this.state.waiting || this.props.archivo.completo}
                  >
                    Ver documento enviado (CURP requerida)
                  </Button>
                </Col>
                <Col xs="12" md="12">
                  <Button
                    className={`${s.secundaryButton} mt-3`}
                    onClick={() => {
                      this.openOrSendDocument(firmante, true);
                    }}
                    block
                    disabled={this.state.waiting || this.props.archivo.completo}
                  >
                    Reenviar solicitud por correo
                  </Button>
                </Col>
                <Col xs="12" md="12">
                  <hr />
                </Col>
              </Row>
            ))}

          <Row className="mt-2">
            <Col xs="12" md="12">
              <Button
                className={`${s.secundaryButton} mt-3`}
                onClick={() => {
                  this.setState({ cincelHardConfirm: false });
                }}
                block
                disabled={this.state.waiting}
              >
                Continuar con la regeneración
              </Button>
            </Col>
          </Row>
        </Container>
      </ModalFrame>
    );
  };
}
